import { Injectable } from '@angular/core';

import { Observable, Subject } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { StreamEvent, StreamEventAgentType, StreamEventSender, StreamEventState } from '../models';

import { AuthenticationService } from './authentication.service';
import { AngularFirestoreCollection, AngularFirestoreQuery, FirestoreService, FirestoreTimestamp } from './firestore';
import { HttpRequestRetryPolicyService, HttpRequestService } from './http';

export type EventStreamEventType =
    | 'CALL_OUTBOUND_STARTED'
    | 'CHAT_STARTED'
    | 'CONVERSATION_CONTROL_FORWARDED'
    | 'CONVERSATION_CONTROL_RELEASED_BY_OPERATOR'
    | 'CONVERSATION_CONTROL_RELEASED'
    | 'CONVERSATION_CONTROL_TAKEN'
    | 'CONVERSATION_CONTROL_TRANSFERRED'
    | 'CONVERSATION_ERROR'
    | 'CONVERSATION_ESCALATED'
    | 'OUTSIDE_BUSINESS_HOURS_TICKET_CLOSED_BY_OPERATOR'
    | 'TICKET_ESCALATED'
    | 'TICKET_REVIEWED'
    | any;

export interface StreamEventsDTO<T extends StreamEvent> {
    events: StreamEventDTO<T>[];
}

export interface StreamEventDTO<T extends StreamEvent> {
    channelId: string;
    tenantId?: string;
    streamId: string;
    event: T;
}

export interface EventStreamEvent extends StreamEvent {
    type: 'EVENT';
    payload: {
        event: {
            type: EventStreamEventType;
        };
    };
}

/**
 * @deprecated This class exists only to support old tickets
 */
export class FailError {
    code: string;
    developerMessage: string;

    fields: {
        hidden: boolean;
        nameLabel: string;
        type: string;
        value: string;
    }[];

    ongoingAction: string;
    type: string;
    userMessage: string;
}

/**
 * This class replaces old FailError class
 */
export class ErrorContext {
    intent: string;

    fields: {
        label: string;
        value: string;
        type: 'STRING' | 'DATE_TIME';
    }[];

    motive: string;
}

export class ConversationErrorEventStreamEvent implements EventStreamEvent {
    channelId: string;
    clientConnectionId: string;
    createdAt: number;
    failErrors: FailError[];
    id: string;

    payload: {
        event: {
            type: 'CONVERSATION_ERROR';
            context: ErrorContext[];
        };
    };

    protocolVersion: string;

    sender: StreamEventSender;

    state: StreamEventState;
    streamId: string;
    type: 'EVENT';
    ticketId: string;
    chatSessionId: string;

    constructor(
        channelId: string,
        clientConnectionId: string,
        createdAt: number,
        failErrors: FailError[],
        id: string,
        streamId: string,
        ticketId: string,
        chatSessionId: string,
        errorContext: ErrorContext[],
        preferredLanguages: string[]
    ) {
        this.channelId = channelId;
        this.clientConnectionId = clientConnectionId;
        this.createdAt = createdAt;
        this.failErrors = failErrors;
        this.id = id;

        this.payload = {
            event: {
                type: 'CONVERSATION_ERROR',
                context: errorContext,
            },
        };

        this.protocolVersion = '1.0.0';

        this.sender = {
            id: 'AiAgent',
            type: 'AI_AGENT',
            preferredLanguages,
        };

        this.state = 'PENDING';
        this.streamId = streamId;
        this.type = 'EVENT';
        this.ticketId = ticketId;
        this.chatSessionId = chatSessionId;
    }
}

export class ConversationEscalatedEventStreamEvent implements EventStreamEvent {
    channelId: string;
    clientConnectionId: string;
    createdAt: number;
    failErrors: FailError[];
    id: string;

    payload: {
        event: {
            type: 'CONVERSATION_ESCALATED';
            context: ErrorContext[];
        };
    };

    protocolVersion: string;

    sender: StreamEventSender;

    state: StreamEventState;
    streamId: string;
    type: 'EVENT';
    ticketId: string;
    chatSessionId?: string;

    constructor(
        channelId: string,
        clientConnectionId: string,
        createdAt: number,
        failErrors: FailError[],
        id: string,
        streamId: string,
        ticketId: string,
        chatSessionId: string,
        errorContext: ErrorContext[],
        preferredLanguages: string[]
    ) {
        this.channelId = channelId;
        this.clientConnectionId = clientConnectionId;
        this.createdAt = createdAt;
        this.failErrors = failErrors;
        this.id = id;

        this.payload = {
            event: {
                type: 'CONVERSATION_ESCALATED',
                context: errorContext,
            },
        };

        this.protocolVersion = '1.0.0';

        this.sender = {
            id: 'AiAgent',
            type: 'AI_AGENT',
            preferredLanguages,
        };

        this.state = 'PENDING';
        this.streamId = streamId;
        this.type = 'EVENT';
        this.ticketId = ticketId;
        this.chatSessionId = chatSessionId;
    }
}

export class TicketEscalatedEventStreamEvent implements EventStreamEvent {
    channelId: string;
    clientConnectionId: string;
    createdAt: number;
    id: string;
    failErrors: FailError[];

    payload: {
        event: {
            agent: StreamEventAgentType;
            context: ErrorContext[];
            type: 'TICKET_ESCALATED';
            ticketId: string;
        };
    };

    protocolVersion: string;

    sender: StreamEventSender;

    state: StreamEventState;
    streamId: string;
    type: 'EVENT';
    ticketId: string;

    constructor(
        channelId: string,
        clientConnectionId: string,
        createdAt: number,
        failErrors: FailError[],
        id: string,
        streamId: string,
        ticketId: string,
        errorContext: ErrorContext[],
        preferredLanguages: string[]
    ) {
        this.channelId = channelId;
        this.clientConnectionId = clientConnectionId;
        this.createdAt = createdAt;
        this.id = id;
        this.failErrors = failErrors;

        this.payload = {
            event: {
                agent: 'OPERATOR',
                context: errorContext,
                type: 'TICKET_ESCALATED',
                ticketId,
            },
        };

        this.protocolVersion = '1.0.0';

        this.sender = {
            id: 'AiAgent',
            type: 'AI_AGENT',
            preferredLanguages,
        };

        this.state = 'PENDING';
        this.streamId = streamId;
        this.type = 'EVENT';
        this.ticketId = ticketId;
    }
}

export class ConversationForwardedEventStreamEvent implements EventStreamEvent {
    channelId: string;
    chatSessionId: string;
    createdAt: number;
    id: string;

    payload: {
        event: {
            type: 'CONVERSATION_CONTROL_FORWARDED';
            context: {
                forwardedTo: string;
                forwardedName: string;
            };
        };
    };

    protocolVersion: string;

    sender: StreamEventSender;

    state: StreamEventState;
    streamId: string;
    ticketId: string;
    type: 'EVENT';
    clientConnectionId?: string;

    constructor(
        channelId: string,
        chatSessionId: string,
        createdAt: number,
        id: string,
        forwardedTo: string,
        forwardedName: string,
        operatorId: string,
        streamId: string,
        ticketId: string,
        preferredLanguages: string[],
        clientConnectionId: string
    ) {
        this.channelId = channelId;
        this.chatSessionId = chatSessionId;
        this.createdAt = createdAt;
        this.id = id;

        this.payload = {
            event: {
                type: 'CONVERSATION_CONTROL_FORWARDED',
                context: {
                    forwardedTo,
                    forwardedName,
                },
            },
        };

        this.protocolVersion = '1.0.0';

        this.sender = {
            id: operatorId,
            type: 'APP_OPERATOR',
            preferredLanguages,
        };

        this.state = 'PENDING';
        this.streamId = streamId;
        this.ticketId = ticketId;
        this.type = 'EVENT';
        this.clientConnectionId = clientConnectionId;
    }
}

export class OperatorOnHoldStartedEventStreamEvent implements EventStreamEvent {
    channelId: string;
    chatSessionId: string;
    createdAt: number;
    id: string;

    payload: {
        event: {
            type: 'OPERATOR_ON_HOLD_STARTED';
            context: {
                operatorId: string;
            };
        };
    };

    protocolVersion: string;
    sender: StreamEventSender;
    state: StreamEventState;
    streamId: string;
    ticketId: string;
    type: 'EVENT';
    clientConnectionId?: string;

    constructor(
        channelId: string,
        chatSessionId: string,
        createdAt: number,
        id: string,
        operatorId: string,
        streamId: string,
        ticketId: string,
        preferredLanguages: string[],
        clientConnectionId: string
    ) {
        this.channelId = channelId;
        this.chatSessionId = chatSessionId;
        this.createdAt = createdAt;
        this.id = id;

        this.payload = {
            event: {
                type: 'OPERATOR_ON_HOLD_STARTED',
                context: {
                    operatorId,
                },
            },
        };

        this.protocolVersion = '1.0.0';

        this.sender = {
            id: operatorId,
            type: 'AI_AGENT',
            preferredLanguages,
        };

        this.state = 'PENDING';
        this.streamId = streamId;
        this.ticketId = ticketId;
        this.type = 'EVENT';
        this.clientConnectionId = clientConnectionId;
    }
}

export class OperatorOnHoldFinishedEventStreamEvent implements EventStreamEvent {
    channelId: string;
    chatSessionId: string;
    createdAt: number;
    id: string;

    payload: {
        event: {
            type: 'OPERATOR_ON_HOLD_FINISHED';
            context: {
                operatorId: string;
            };
        };
    };

    protocolVersion: string;
    sender: StreamEventSender;
    state: StreamEventState;
    streamId: string;
    ticketId: string;
    type: 'EVENT';
    clientConnectionId?: string;

    constructor(
        channelId: string,
        chatSessionId: string,
        createdAt: number,
        id: string,
        operatorId: string,
        streamId: string,
        ticketId: string,
        preferredLanguages: string[],
        clientConnectionId: string
    ) {
        this.channelId = channelId;
        this.chatSessionId = chatSessionId;
        this.createdAt = createdAt;
        this.id = id;

        this.payload = {
            event: {
                type: 'OPERATOR_ON_HOLD_FINISHED',
                context: {
                    operatorId,
                },
            },
        };

        this.protocolVersion = '1.0.0';

        this.sender = {
            id: operatorId,
            type: 'AI_AGENT',
            preferredLanguages,
        };

        this.state = 'PENDING';
        this.streamId = streamId;
        this.ticketId = ticketId;
        this.type = 'EVENT';
        this.clientConnectionId = clientConnectionId;
    }
}

export class OperatorOnMuteStartedEventStreamEvent implements EventStreamEvent {
    channelId: string;
    chatSessionId: string;
    createdAt: number;
    id: string;

    payload: {
        event: {
            type: 'OPERATOR_ON_MUTE_STARTED';
            context: {
                operatorId: string;
            };
        };
    };

    protocolVersion: string;
    sender: StreamEventSender;
    state: StreamEventState;
    streamId: string;
    ticketId: string;
    type: 'EVENT';
    clientConnectionId?: string;

    constructor(
        channelId: string,
        chatSessionId: string,
        createdAt: number,
        id: string,
        operatorId: string,
        streamId: string,
        ticketId: string,
        preferredLanguages: string[],
        clientConnectionId: string
    ) {
        this.channelId = channelId;
        this.chatSessionId = chatSessionId;
        this.createdAt = createdAt;
        this.id = id;

        this.payload = {
            event: {
                type: 'OPERATOR_ON_MUTE_STARTED',
                context: {
                    operatorId,
                },
            },
        };

        this.protocolVersion = '1.0.0';

        this.sender = {
            id: operatorId,
            type: 'AI_AGENT',
            preferredLanguages,
        };

        this.state = 'PENDING';
        this.streamId = streamId;
        this.ticketId = ticketId;
        this.type = 'EVENT';
        this.clientConnectionId = clientConnectionId;
    }
}

export class OperatorOnMuteFinishedEventStreamEvent implements EventStreamEvent {
    channelId: string;
    chatSessionId: string;
    createdAt: number;
    id: string;

    payload: {
        event: {
            type: 'OPERATOR_ON_MUTE_FINISHED';
            context: {
                operatorId: string;
            };
        };
    };

    protocolVersion: string;
    sender: StreamEventSender;
    state: StreamEventState;
    streamId: string;
    ticketId: string;
    type: 'EVENT';
    clientConnectionId?: string;

    constructor(
        channelId: string,
        chatSessionId: string,
        createdAt: number,
        id: string,
        operatorId: string,
        streamId: string,
        ticketId: string,
        preferredLanguages: string[],
        clientConnectionId: string
    ) {
        this.channelId = channelId;
        this.chatSessionId = chatSessionId;
        this.createdAt = createdAt;
        this.id = id;

        this.payload = {
            event: {
                type: 'OPERATOR_ON_MUTE_FINISHED',
                context: {
                    operatorId,
                },
            },
        };

        this.protocolVersion = '1.0.0';

        this.sender = {
            id: operatorId,
            type: 'AI_AGENT',
            preferredLanguages,
        };

        this.state = 'PENDING';
        this.streamId = streamId;
        this.ticketId = ticketId;
        this.type = 'EVENT';
        this.clientConnectionId = clientConnectionId;
    }
}

export class TicketReviewedEventStreamEvent implements EventStreamEvent {
    channelId: string;
    chatSessionId: string;
    createdAt: number;
    id: string;

    payload: {
        event: {
            type: 'TICKET_REVIEWED';
            context: {
                operatorId: string;
                operatorName: string;
            };
        };
    };

    protocolVersion: string;

    sender: StreamEventSender;

    state: StreamEventState;
    streamId: string;
    ticketId: string;
    type: 'EVENT';
    clientConnectionId?: string;

    constructor(
        channelId: string,
        chatSessionId: string,
        createdAt: number,
        id: string,
        operatorId: string,
        operatorName: string,
        streamId: string,
        ticketId: string,
        preferredLanguages: string[],
        clientConnectionId: string
    ) {
        this.channelId = channelId;
        this.chatSessionId = chatSessionId;
        this.createdAt = createdAt;
        this.id = id;

        this.payload = {
            event: {
                type: 'TICKET_REVIEWED',
                context: {
                    operatorId,
                    operatorName,
                },
            },
        };

        this.protocolVersion = '1.0.0';

        this.sender = {
            id: operatorId,
            type: 'APP_OPERATOR',
            preferredLanguages,
        };

        this.state = 'PENDING';
        this.streamId = streamId;
        this.ticketId = ticketId;
        this.type = 'EVENT';
        this.clientConnectionId = clientConnectionId;
    }
}

export class ConversationControlTakenEventStreamEvent implements EventStreamEvent {
    id: string;
    createdAt: number;
    protocolVersion: string;
    state: StreamEventState;
    type: 'EVENT';
    streamId: string;
    channelId: string;
    sender: StreamEventSender;
    payload: {
        event: {
            type: 'CONVERSATION_CONTROL_TAKEN';
            context: {
                operatorId: string;
                operatorName: string;
            };
        };
    };
    ticketId: string;
    chatSessionId?: string;
    clientConnectionId?: string;

    constructor(
        id: string,
        streamId: string,
        channelId: string,
        operatorId: string,
        operatorName: string,
        createdAt: number,
        ticketId: string,
        chatSessionId: string,
        preferredLanguages: string[],
        clientConnectionId: string
    ) {
        this.id = id;
        this.type = 'EVENT';
        this.createdAt = createdAt;
        this.state = 'PENDING';
        this.protocolVersion = '1.0.0';
        this.streamId = streamId;
        this.channelId = channelId;

        this.sender = {
            id: operatorId,
            type: 'APP_OPERATOR',
            preferredLanguages,
        };

        this.payload = {
            event: {
                type: 'CONVERSATION_CONTROL_TAKEN',
                context: {
                    operatorId,
                    operatorName,
                },
            },
        };
        this.ticketId = ticketId;
        this.chatSessionId = chatSessionId;
        this.clientConnectionId = clientConnectionId;
    }
}

export class ConversationControlTakenClearEventStreamEvent implements EventStreamEvent {
    id: string;
    createdAt: number;
    protocolVersion: string;
    state: StreamEventState;
    type: 'EVENT';
    streamId: string;
    channelId: string;
    sender: StreamEventSender;
    payload: {
        event: {
            type: 'CONVERSATION_CONTROL_TAKEN_CLEAR';
            context: {
                operatorId: string;
                operatorName: string;
            };
        };
    };
    ticketId: string;
    chatSessionId?: string;
    clientConnectionId?: string;

    constructor(
        id: string,
        streamId: string,
        channelId: string,
        operatorId: string,
        operatorName: string,
        createdAt: number,
        ticketId: string,
        chatSessionId: string,
        preferredLanguages: string[],
        clientConnectionId: string
    ) {
        this.id = id;
        this.type = 'EVENT';
        this.createdAt = createdAt;
        this.state = 'PENDING';
        this.protocolVersion = '1.0.0';
        this.streamId = streamId;
        this.channelId = channelId;

        this.sender = {
            id: operatorId,
            type: 'APP_OPERATOR',
            preferredLanguages,
        };

        this.payload = {
            event: {
                type: 'CONVERSATION_CONTROL_TAKEN_CLEAR',
                context: {
                    operatorId,
                    operatorName,
                },
            },
        };
        this.ticketId = ticketId;
        this.chatSessionId = chatSessionId;
        this.clientConnectionId = clientConnectionId;
    }
}

export class ConversationControlTransferedEventStreamEvent implements EventStreamEvent {
    id: string;
    state: StreamEventState;
    createdAt: number;
    protocolVersion: string;
    sender: StreamEventSender;
    type: 'EVENT';
    streamId: string;
    channelId: string;
    payload: {
        event: {
            type: 'CONVERSATION_CONTROL_TRANSFERRED';
            context: {
                oldOperatorId: string;
                oldOperatorName: string;
                newOperatorId: string;
                newOperatorName: string;
                transferredById: string;
                transferredByName: string;
            };
        };
    };
    ticketId: string;
    chatSessionId?: string;
    clientConnectionId?: string;

    constructor(
        id: string,
        streamId: string,
        channelId: string,
        currentOperatorId: string,
        currentOperatorName: string,
        newOperatorId: string,
        newOperatorName: string,
        transferredById: string,
        transferredByName: string,
        createdAt: number,
        ticketId: string,
        chatSessionId: string,
        preferredLanguages: string[],
        clientConnectionId: string
    ) {
        this.id = id;
        this.type = 'EVENT';
        this.state = 'PENDING';
        this.streamId = streamId;
        this.channelId = channelId;
        this.createdAt = createdAt;
        this.protocolVersion = '1.0.0';
        this.sender = {
            id: currentOperatorId,
            type: 'APP_OPERATOR',
            preferredLanguages,
        };
        this.payload = {
            event: {
                type: 'CONVERSATION_CONTROL_TRANSFERRED',
                context: {
                    oldOperatorId: currentOperatorId,
                    oldOperatorName: currentOperatorName,
                    newOperatorId,
                    newOperatorName,
                    transferredById,
                    transferredByName,
                },
            },
        };
        this.ticketId = ticketId;
        this.chatSessionId = chatSessionId;
        this.clientConnectionId = clientConnectionId;
    }
}

export class OutsideBusinessHoursTicketClosedByOperatorStreamEvent implements EventStreamEvent {
    id: string;
    state: StreamEventState;
    createdAt: number;
    protocolVersion: string;
    sender: StreamEventSender;
    type: 'EVENT';
    streamId: string;
    channelId: string;
    payload: {
        event: {
            type: 'OUTSIDE_BUSINESS_HOURS_TICKET_CLOSED_BY_OPERATOR';
            context: {
                operatorId: string;
                operatorName: string;
                motiveId: string;
            };
        };
    };
    ticketId: string;
    chatSessionId?: string;
    clientConnectionId?: string;

    constructor(
        id: string,
        streamId: string,
        channelId: string,
        operatorId: string,
        operatorName: string,
        motiveId: string,
        createdAt: number,
        ticketId: string,
        chatSessionId: string,
        preferredLanguages: string[],
        clientConnectionId: string
    ) {
        this.id = id;
        this.type = 'EVENT';
        this.state = 'PENDING';
        this.createdAt = createdAt;
        this.protocolVersion = '1.0.0';
        this.streamId = streamId;
        this.channelId = channelId;
        this.sender = {
            id: operatorId,
            type: 'APP_OPERATOR',
            preferredLanguages,
        };
        this.payload = {
            event: {
                type: 'OUTSIDE_BUSINESS_HOURS_TICKET_CLOSED_BY_OPERATOR',
                context: {
                    operatorId,
                    operatorName,
                    motiveId,
                },
            },
        };
        this.ticketId = ticketId;
        this.chatSessionId = chatSessionId;
        this.clientConnectionId = clientConnectionId;
    }
}

export class CallOutboundStartedStreamEvent implements EventStreamEvent {
    id: string;
    state: StreamEventState;
    createdAt: number;
    protocolVersion: string;
    sender: StreamEventSender;
    type: 'EVENT';
    streamId: string;
    channelId: string;
    payload: {
        event: {
            type: 'CALL_OUTBOUND_STARTED';
            context: {
                operatorId: string;
                operatorName: string;
            };
        };
    };
    ticketId: string;
    chatSessionId?: string;
    clientConnectionId?: string;

    constructor(
        id: string,
        streamId: string,
        channelId: string,
        operatorId: string,
        operatorName: string,
        createdAt: number,
        ticketId: string,
        chatSessionId: string,
        preferredLanguages: string[],
        clientConnectionId: string
    ) {
        this.id = id;
        this.type = 'EVENT';
        this.state = 'PENDING';
        this.createdAt = createdAt;
        this.protocolVersion = '1.0.0';
        this.streamId = streamId;
        this.channelId = channelId;
        this.sender = {
            id: operatorId,
            type: 'APP_OPERATOR',
            preferredLanguages,
        };
        this.payload = {
            event: {
                type: 'CALL_OUTBOUND_STARTED',
                context: {
                    operatorId,
                    operatorName,
                },
            },
        };
        this.ticketId = ticketId;
        this.chatSessionId = chatSessionId;
        this.clientConnectionId = clientConnectionId;
    }
}

export class ConversationControlReleasedEventStreamEvent implements EventStreamEvent {
    id: string;
    state: StreamEventState;
    createdAt: number;
    protocolVersion: string;
    sender: StreamEventSender;
    type: 'EVENT';
    streamId: string;
    channelId: string;
    payload: {
        event: {
            type: 'CONVERSATION_CONTROL_RELEASED';
            context: {
                operatorId: string;
                operatorName: string;
            };
        };
    };
    ticketId: string;
    chatSessionId?: string;
    clientConnectionId?: string;

    constructor(
        id: string,
        streamId: string,
        channelId: string,
        operatorId: string,
        operatorName: string,
        createdAt: number,
        ticketId: string,
        chatSessionId: string,
        preferredLanguages: string[],
        clientConnectionId: string
    ) {
        this.id = id;
        this.type = 'EVENT';
        this.state = 'PENDING';
        this.createdAt = createdAt;
        this.protocolVersion = '1.0.0';
        this.streamId = streamId;
        this.channelId = channelId;
        this.sender = {
            id: operatorId,
            type: 'APP_OPERATOR',
            preferredLanguages,
        };
        this.payload = {
            event: {
                type: 'CONVERSATION_CONTROL_RELEASED',
                context: {
                    operatorId,
                    operatorName,
                },
            },
        };
        this.ticketId = ticketId;
        this.chatSessionId = chatSessionId;
        this.clientConnectionId = clientConnectionId;
    }
}

export class ConversationControlReleasedByOperatorEventStreamEvent implements EventStreamEvent {
    id: string;
    state: StreamEventState;
    createdAt: number;
    protocolVersion: string;
    sender: StreamEventSender;
    type: 'EVENT';
    streamId: string;
    channelId: string;
    payload: {
        event: {
            type: 'CONVERSATION_CONTROL_RELEASED_BY_OPERATOR';
            context: {
                operatorId: string;
                operatorName: string;
                reason: 'SESSION_LOGOUT';
            };
        };
    };
    ticketId: string;
    chatSessionId?: string;
    clientConnectionId?: string;

    constructor(
        id: string,
        streamId: string,
        channelId: string,
        operatorId: string,
        operatorName: string,
        createdAt: number,
        ticketId: string,
        chatSessionId: string,
        preferredLanguages: string[],
        clientConnectionId: string
    ) {
        this.id = id;
        this.type = 'EVENT';
        this.state = 'PENDING';
        this.createdAt = createdAt;
        this.protocolVersion = '1.0.0';
        this.streamId = streamId;
        this.channelId = channelId;
        this.sender = {
            id: operatorId,
            type: 'APP_OPERATOR',
            preferredLanguages,
        };
        this.payload = {
            event: {
                type: 'CONVERSATION_CONTROL_RELEASED_BY_OPERATOR',
                context: {
                    operatorId,
                    operatorName,
                    reason: 'SESSION_LOGOUT',
                },
            },
        };
        this.ticketId = ticketId;
        this.chatSessionId = chatSessionId;
        this.clientConnectionId = clientConnectionId;
    }
}

export type ChatStreamEventAttachmentType = 'TEXT';

export interface ChatStreamEventAttachment {
    id: string;
    type: ChatStreamEventAttachmentType;
    value: string;
    language: string;
}

export interface ChatStreamEvent extends StreamEvent {
    type: 'CHAT';
    payload: {
        attachments: ChatStreamEventAttachment[];
    };
}

export class TextChatStreamEvent implements ChatStreamEvent {
    type: 'CHAT';
    id: string;
    state: StreamEventState;
    createdAt: number;
    protocolVersion: string;
    streamId: string;
    channelId: string;
    sender: StreamEventSender;
    payload: {
        attachments: ChatStreamEventAttachment[];
    };
    ticketId: string;
    chatSessionId?: string;

    constructor(
        id: string,
        streamId: string,
        channelId: string,
        operatorId: string,
        createdAt: number,
        message: string,
        ticketId: string,
        chatSessionId: string,
        preferredLanguages: string[],
        language: string
    ) {
        this.type = 'CHAT';
        this.id = id;
        this.state = 'PENDING';
        this.createdAt = createdAt;
        this.protocolVersion = '1.0.0';
        this.streamId = streamId;
        this.channelId = channelId;
        this.sender = {
            id: operatorId,
            type: 'APP_OPERATOR',
            preferredLanguages,
        };
        this.payload = {
            attachments: [
                {
                    id,
                    type: 'TEXT',
                    value: message,
                    language,
                },
            ],
        };
        this.ticketId = ticketId;
        this.chatSessionId = chatSessionId;
    }
}

export interface CallStatisticField {
    name: string;
    value: any;
    type: 'STRING' | 'DATE_TIME' | 'NUMBER';
    unit?: string;
    title?: string;
}

export class CallStatisticsEventStreamEvent implements EventStreamEvent {
    channelId: string;
    createdAt: number;
    id: string;

    payload: {
        event: {
            type: 'CALL_STATISTICS';
            callStatistics: CallStatisticField[];
        };
    };

    protocolVersion: string;

    sender: StreamEventSender;

    state: StreamEventState;
    streamId: string;
    type: 'EVENT';
    ticketId: string;

    constructor(
        channelId: string,
        createdAt: number,
        id: string,
        streamId: string,
        ticketId: string,
        preferredLanguages: string[],
        callStatistics: CallStatisticField[]
    ) {
        this.channelId = channelId;
        this.createdAt = createdAt;
        this.id = id;

        this.payload = {
            event: {
                type: 'CALL_STATISTICS',
                callStatistics,
            },
        };

        this.protocolVersion = '1.0.0';

        this.sender = {
            id: 'AiAgent',
            type: 'AI_AGENT',
            preferredLanguages,
        };

        this.state = 'PENDING';
        this.streamId = streamId;
        this.type = 'EVENT';
        this.ticketId = ticketId;
    }
}

@Injectable({
    providedIn: 'root',
})
export class StreamEventsService {
    constructor(
        private firestoreService: FirestoreService,
        private authenticationService: AuthenticationService,
        private httpService: HttpRequestService,
        private httpRetryPolicyService: HttpRequestRetryPolicyService
    ) {}

    /**
     * Creates an observable that will provide all the events between the defined first and last events.
     * When the lastStreamEventId is not defined, the observable will provide all the event between the
     * first stream event and the more recent event.
     */
    getStreamEvents(
        channelId: string,
        streamId: string,
        ticketId: string,
        firstStreamEventId?: string,
        lastStreamEventId?: string,
        eventsToLoad?: number,
        includeLastEvent?: boolean,
        useSameQuery?: boolean
    ): Observable<{ ticketId: string; streamEvents: StreamEvent[] }> {
        const subject = new Subject<{ ticketId: string; streamEvents: StreamEvent[] }>();

        let subjectUnsubscribeFn: () => void;

        // get promises to get the first and last event, to defined the stream events list boundaries
        const firstStreamEventPromise = this.getStreamEvent(channelId, streamId, firstStreamEventId);
        const lastStreamEventPromise = this.getStreamEvent(channelId, streamId, lastStreamEventId);

        Promise.all([firstStreamEventPromise, lastStreamEventPromise]).then((res) => {
            const firstStreamEvent = res[0];
            const lastStreamEvent = res[1];

            // get collection to the defined stream
            const collection = this.collection(channelId, streamId);

            // query that only
            let query: AngularFirestoreQuery;
            const queryOrRef = () => (query ? query : collection.ref);

            if (firstStreamEvent) {
                if (useSameQuery) {
                    query = queryOrRef().where('createdAt', '<=', firstStreamEvent.createdAt);
                } else {
                    query = queryOrRef().where('createdAt', '>=', firstStreamEvent.createdAt);
                }
            }

            if (lastStreamEvent) {
                const includeLast = includeLastEvent ? '<=' : '<';
                query = queryOrRef().where('createdAt', includeLast, lastStreamEvent.createdAt);
            }

            // order by time
            query = queryOrRef().orderBy('createdAt', 'asc');

            if (eventsToLoad) {
                query = queryOrRef().limitToLast(eventsToLoad);
            }

            // subscribe the query changes
            subjectUnsubscribeFn = query.onSnapshot((onSnapshot) => {
                // get the Stream Events updates and send them to any observables subscriber
                const events = onSnapshot
                    .docChanges()
                    .filter((d) => {
                        return d.type !== 'removed';
                    })
                    .map((d) => {
                        const event = d.doc.data() as StreamEvent;
                        event.id = d.doc.id;

                        return this.normalizeEventCreatedAt(event);
                    });
                subject.next({ ticketId, streamEvents: events });
            });
        });

        // unsubscribe the query snapshot when the subject is finalized
        return subject.pipe(
            finalize(() => {
                if (subjectUnsubscribeFn) {
                    subjectUnsubscribeFn();
                }
            })
        );
    }

    normalizeEventCreatedAt(event?: StreamEvent): StreamEvent {
        if (event && event.serverTimestamp && event.serverTimestamp.seconds) {
            const convertedTimestamp = new FirestoreTimestamp(event.serverTimestamp.seconds, event.serverTimestamp.nanoseconds);
            event.createdAt = convertedTimestamp.toMillis() / 1000;
        }

        return event;
    }

    async sendStreamEvent<T extends StreamEvent>(event: StreamEventDTO<T>): Promise<void> {
        if (!event) {
            throw new Error('All properties are mandatory');
        }

        await this.sendStreamEvents([event]);
    }

    async sendStreamEvents<T extends StreamEvent>(events: StreamEventDTO<T>[]): Promise<void> {
        if (!events || events.length === 0) {
            throw new Error('All properties are mandatory');
        }

        const tenantId = this.authenticationService.user.tenantId;
        events.forEach((ev) => {
            ev.tenantId = tenantId;
        });

        const eventsDto: StreamEventsDTO<T> = { events };

        await this.httpService.post<any>('/api/streamEvents', eventsDto, this.httpRetryPolicyService.noRetryPolicy());
    }

    private async getStreamEvent(channelId: string, streamId: string, streamEventId: string): Promise<StreamEvent> {
        if (!streamEventId || !channelId || !streamId) {
            return null;
        }

        const collection = this.collection(channelId, streamId);
        let streamEvent: StreamEvent;

        try {
            const streamEventDoc = await collection.doc<StreamEvent>(streamEventId).get().toPromise();
            if (streamEventDoc.exists) {
                streamEvent = streamEventDoc.data() as StreamEvent;
                streamEvent.id = streamEventDoc.id;
            }
        } catch (error) {
            console.error(error);
        }

        return streamEvent;
    }

    private collection(channelId: string, streamId: string): AngularFirestoreCollection<StreamEvent> {
        const path = `channels/${channelId}/streams/${streamId}/events`;
        const collection = this.firestoreService.tenantDependentCollection<StreamEvent>(this.authenticationService.user.tenantId, path);

        return collection;
    }
}
